import { Component, createSignal, Show } from "solid-js";
import { formatId, isExternalIdFormat } from "../utils/formatId";

interface IdBadgeProps {
  id: string;
  /** Optional prefix label, e.g. "Client" */
  label?: string;
  /** Number of characters to show (default 8) */
  len?: number;
}

export const IdBadge: Component<IdBadgeProps> = (props) => {
  const [copied, setCopied] = createSignal(false);
  const [showTooltip, setShowTooltip] = createSignal(false);

  const handleClick = async (e: MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(props.id);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Fallback: use deprecated execCommand for environments where clipboard API is unavailable
      try {
        const textarea = document.createElement("textarea");
        textarea.value = props.id;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      } catch {
        // Both methods failed - no feedback needed for inline badge
      }
    }
  };

  const displayId = () => {
    // External IDs are already compact, show more chars
    const effectiveLen = isExternalIdFormat(props.id) ? (props.len ?? 12) : (props.len ?? 8);
    return formatId(props.id, effectiveLen);
  };

  return (
    <span
      style={{ position: "relative", display: "inline-flex", "align-items": "center", gap: "4px" }}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <Show when={props.label}>
        <span style={{ "font-size": "var(--text-xs)", color: "var(--text-secondary)" }}>
          {props.label}:
        </span>
      </Show>
      <code
        onClick={handleClick}
        style={{
          "font-size": "var(--text-xs)",
          "font-family": "var(--font-mono, ui-monospace, monospace)",
          padding: "1px 6px",
          "border-radius": "4px",
          background: "var(--bg-secondary)",
          color: "var(--text-primary)",
          cursor: "pointer",
          transition: "background 0.15s ease",
          "user-select": "none",
          border: "1px solid var(--border-light, transparent)",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "var(--bg-primary)";
          e.currentTarget.style.borderColor = "var(--border-medium)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "var(--bg-secondary)";
          e.currentTarget.style.borderColor = "var(--border-light, transparent)";
        }}
      >
        {displayId()}
      </code>

      {/* Tooltip */}
      <Show when={showTooltip()}>
        <div
          style={{
            position: "absolute",
            bottom: "calc(100% + 6px)",
            left: "50%",
            transform: "translateX(-50%)",
            padding: "4px 8px",
            "border-radius": "4px",
            background: "var(--text-primary)",
            color: "var(--bg-primary)",
            "font-size": "11px",
            "font-family": "var(--font-mono, ui-monospace, monospace)",
            "white-space": "nowrap",
            "z-index": "1000",
            "pointer-events": "none",
            "box-shadow": "0 2px 8px rgba(0,0,0,0.15)",
          }}
        >
          {copied() ? "Copied!" : props.id}
        </div>
      </Show>
    </span>
  );
};
